import { LitElement, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import type { FileContext } from "api-contracts/rpc/getFileUploadUrl";
import { DragDropController } from "ui-common/drag-drop-controller";
import { FileUploadError, formatUploadError, uploadFile } from "ui-common/services/file-upload";

import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./media-uploader.css";

import { t, translate } from "../../../apps/backoffice/src/translations/i18n";
import "../base-image-cropper/base-image-cropper";
import "../button/wow-button";
import { NotificationService } from "../notification/notification-service";
import type { ViewportType } from "../base-image-cropper/base-image-cropper";
import { isVideoMimeType } from "helpers/fileValidation";

@customElement("media-uploader")
export class MediaUploader extends LitElement {
  static override styles = styles;

  private internals: ElementInternals;
  static formAssociated = true;

  private dragDrop = new DragDropController(this, (file) => this.processDroppedFile(file));

  @property({ type: String })
  context: FileContext = "cover_image";

  @property({ type: String })
  currentFileUrl?: string;

  @property({ type: String })
  currentFileType?: "image" | "video";

  @property({ type: String })
  label?: string = translate("fileUpload.label");

  @property({ type: String })
  subtitle = "";

  @property({ type: String })
  viewport: ViewportType = "square";

  @state()
  private uploading = false;

  @state()
  private uploadProgress = 0;

  @state()
  private selectedFile?: File;

  @state()
  private isVideoBackground = false;

  @state()
  private uploadedFileMimeType?: string;

  @state()
  private currentState: "initial" | "cropping" = "initial";

  @state()
  private imageElement?: HTMLImageElement;

  @state()
  private previousFileUrl?: string;

  @state()
  private previousValue?: string;

  @property({ type: Boolean })
  showCameraOverlay = true;

  @property({ type: String })
  cameraOverlayPosition: "center" | "top-right" = "center";

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private _value: string = "";

  // Form API: Get current value (file ID)
  get value(): string {
    return this._value;
  }

  // Form API: Set value programmatically
  set value(fileId: string) {
    this._value = fileId;
    this.internals.setFormValue(fileId);
    this.requestUpdate();
  }

  // Reuse existing video detection utility
  private updateVideoState() {
    if (this.uploadedFileMimeType) {
      this.isVideoBackground = isVideoMimeType(this.uploadedFileMimeType);
    } else if (this.currentFileUrl) {
      this.isVideoBackground = this.detectVideoFromUrl(this.currentFileUrl);
    } else {
      this.isVideoBackground = false;
    }
  }

  // Simple video detection from URL
  private detectVideoFromUrl(url: string): boolean {
    if (!url) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
    const urlLower = url.toLowerCase();
    return videoExtensions.some((ext) => urlLower.endsWith(ext));
  }

  private validateSafariVideoCodec(file: File): boolean {
    const video = document.createElement("video");

    // Safari-supported formats: H.264 baseline + AAC
    const supported =
      video.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') ||
      video.canPlayType('video/mp4; codecs="avc1.4D401E, mp4a.40.2"');

    if (!supported) return false;

    // Just ensure file extension / MIME matches allowed types
    return file.type === "video/mp4" || file.type === "video/quicktime";
  }

  private handleFileInput = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      // User cancelled the file picker - dispatch cancel event
      this.dispatchEvent(
        new CustomEvent("cancel", {
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    const isPlayable = this.validateSafariVideoCodec(file);
    if (file.type.startsWith("video/") && !isPlayable) {
      NotificationService.error("Unsupported video codec. Please upload H.264 + AAC.");
      return;
    }

    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: { error: "Please select an image or video file" },
          bubbles: true,
        }),
      );
      return;
    }

    this.selectedFile = file;
    this.uploadedFileMimeType = file.type;

    if (isVideoMimeType(file.type)) {
      // For videos, clear preview immediately and upload directly
      this.currentFileUrl = undefined;
      this.updateVideoState();
      this.handleFileSelect(file);
    } else {
      // For images, store previous state before clearing and go to cropping
      this.previousFileUrl = this.currentFileUrl;
      this.previousValue = this._value;
      this.currentFileUrl = undefined;
      this.updateVideoState();
      this.loadImage(file);
    }

    input.value = "";
  };

  private loadImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        this.imageElement = img;
        this.currentState = "cropping";
        this.requestUpdate();
        // Dispatch crop-started event when cropper actually opens
        this.dispatchEvent(
          new CustomEvent("crop-started", {
            bubbles: true,
            composed: true,
          }),
        );
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  private handleCancel = () => {
    this.currentState = "initial";
    this.selectedFile = undefined;
    this.imageElement = undefined;

    // Restore previous state if cropping was cancelled
    if (this.previousFileUrl !== undefined) {
      this.currentFileUrl = this.previousFileUrl;
      this.previousFileUrl = undefined;
    }
    if (this.previousValue !== undefined) {
      this._value = this.previousValue;
      this.internals.setFormValue(this.previousValue);
      this.previousValue = undefined;
    }

    this.updateVideoState();

    // Dispatch cancel event to notify parent components
    this.dispatchEvent(
      new CustomEvent("cancel", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  // Reuse existing upload functionality
  private async handleFileSelect(file: File) {
    try {
      //this.internals.validationMessage
      //this.me
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadedFileMimeType = file.type;

      this.internals.setValidity({});

      // Use existing uploadFile service with progress tracking
      const result = await uploadFile(file, this.context, {
        onProgress: async (progress) => {
          this.uploadProgress = Math.round(progress);
          await this.updateComplete;
        },
      });

      this.currentFileUrl = result.url;
      this._value = result.fileId;
      this.internals.setFormValue(result.fileId);

      await this.updateComplete;
      this.updateVideoState();
      this.currentState = "initial";

      this.dispatchEvent(
        new CustomEvent("change", {
          detail: {
            fileId: result.fileId,
            imageUrl: result.url,
            fileType: this.isVideoBackground ? "video" : "image",
          },
          bubbles: true,
        }),
      );
    } catch (error) {
      // Reuse existing error handling
      if (error instanceof FileUploadError) {
        console.error("Upload error:", formatUploadError(error));
        NotificationService.error(formatUploadError(error));
      }
      this.internals.setValidity({ customError: true }, error instanceof Error ? error.message : "Upload failed");
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: { error: error instanceof Error ? error.message : "Upload failed" },
          bubbles: true,
        }),
      );
    } finally {
      this.uploading = false;
      this.uploadProgress = 0;
    }
  }

  // Handles save event from base-image-cropper
  private handleBaseCropperSave = async (event: CustomEvent) => {
    if (this.uploading) return;
    try {
      this.uploading = true;
      this.uploadProgress = 0;
      const croppedFile = event.detail?.file;
      if (!croppedFile) {
        throw new Error("Failed to generate cropped file");
      }
      const result = await uploadFile(croppedFile, this.context, {
        onProgress: (progress) => {
          this.uploadProgress = Math.round(progress);
        },
      });
      this.currentFileUrl = result.url;
      this._value = result.fileId;
      this.internals.setFormValue(result.fileId);
      this.internals.setValidity({});
      this.currentState = "initial";
      this.updateVideoState();

      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            fileId: result.fileId,
            imageUrl: result.url,
            fileType: "image",
          },
        }),
      );
      NotificationService.success("Image uploaded successfully!");
    } catch (error) {
      if (error instanceof FileUploadError) {
        NotificationService.error(formatUploadError(error));
      } else {
        NotificationService.error("Failed to upload image. Please try again.");
      }
      this.internals.setValidity({ customError: true }, "Failed to upload image. Please try again.");
    } finally {
      this.uploading = false;
      this.uploadProgress = 0;
    }
  };

  public setCustomValidity(error?: string) {
    if (error) {
      this.internals.setValidity({ customError: true }, error);
    } else {
      this.internals.setValidity({});
    }
    this.requestUpdate();
  }

  private renderVideoPreview() {
    this.updateVideoState();
    if (!this.currentFileUrl || !this.isVideoBackground) return nothing;

    const videoSrc = getAssetUrl(this.currentFileUrl) || "";

    return html`
      <video
        class="media-preview-video ${this.viewport === "cover" ? "fit-auto-height" : ""}"
        loop
        autoplay
        fetchpriority="high"
        muted
        playsinline
        preload="auto"
        src=${videoSrc}
        @loadeddata=${(e: Event) => {
          requestAnimationFrame(() => {
            const video = e.target as HTMLVideoElement;
            if (!video || !video.play) return;
            video.play().catch((err) => console.error("Video play failed:", err));
          });
        }}
        @error=${(e: Event) => console.error("Video error:", (e.target as HTMLVideoElement)?.error)}
      >
        Your browser does not support the video tag.
      </video>
    `;
  }

  private renderImagePreview() {
    if (!this.currentFileUrl || this.isVideoBackground) return nothing;

    const src = getAssetUrl(this.currentFileUrl);
    return html`<img src=${src} alt="Media preview" class="media-preview-image" />`;
  }

  private renderFallbackPlaceholder() {
    if (!this.showCameraOverlay) return nothing;

    if (this.cameraOverlayPosition === "center") {
      return html`
        <div class="upload-placeholder">
          <ph-camera class="camera-icon" size="20" weight="regular" color="currentColor"></ph-camera>
          <span class="upload-label">${this.label} </span>
          ${this.subtitle ? html`<span class="upload-subtitle">${this.subtitle}</span>` : nothing}
        </div>
      `;
    } else {
      return html`
        <div class="upload-placeholder top-right">
          <ph-camera class="camera-icon" size="20" weight="regular" color="currentColor"></ph-camera>
        </div>
      `;
    }
  }

  private renderCurrentFile() {
    if (!this.currentFileUrl) {
      return this.renderFallbackPlaceholder();
    }

    const src = getAssetUrl(this.currentFileUrl);
    if (!src) {
      return this.renderFallbackPlaceholder();
    }

    return html`
      <div class="current-file">
        <div class="preview-container">${this.renderVideoPreview()} ${this.renderImagePreview()}</div>
        ${this.showCameraOverlay
          ? html`${this.cameraOverlayPosition === "center"
              ? html`<div class="upload-overlay">${this.renderFallbackPlaceholder()}</div>`
              : this.renderFallbackPlaceholder()}`
          : nothing}
      </div>
    `;
  }

  private renderInitial() {
    return html`
      ${this.label ? html`<label class="uploader-label">${this.label}</label>` : nothing}
      <div class="media-uploader">
        <div
          class="upload-container ${this.uploading ? "uploading" : ""} ${this.dragDrop.isDragging
            ? "drag-over"
            : ""} ${this.currentFileType === "video" ? "is-video" : ""}"
          @click=${this.handleUploadClick}
          @dragover=${this.dragDrop.handleDragOver}
          @dragleave=${this.dragDrop.handleDragLeave}
          @drop=${this.dragDrop.handleDrop}
        >
          ${this.uploading
            ? html`
                <div class="uploading-state">
                  <span class="upload-text">
                    ${this.uploadProgress >= 0
                      ? `${translate("fileUpload.uploading")} ${this.uploadProgress}%`
                      : t("fileUpload.uploading")}
                  </span>
                </div>
              `
            : nothing}
          ${this.renderCurrentFile()}
          <input
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            @change=${this.handleFileInput}
            style="display: none;"
          />
        </div>

        ${this.internals.validationMessage
          ? html`<span class="error-message">${this.internals.validationMessage}</span>`
          : ""}
      </div>
    `;
  }

  private renderCropping() {
    return html`
      <base-image-cropper
        .open=${true}
        .imageUrl=${this.imageElement?.src}
        .viewport=${this.viewport}
        .headerText=${"Crop image"}
        .footerButtonText=${this.uploading
          ? this.uploadProgress > 0
            ? `Uploading... ${this.uploadProgress}%`
            : "Uploading..."
          : "Save"}
        @cropper-save=${this.handleBaseCropperSave}
        @cropper-cancel=${this.handleCancel}
      ></base-image-cropper>
    `;
  }

  private handleUploadClick = () => {
    if (this.uploading) return;
    const input = this.shadowRoot?.querySelector('input[type="file"]') as HTMLInputElement;
    input?.click();
  };

  private processDroppedFile(file: File) {
    this.selectedFile = file;
    this.uploadedFileMimeType = file.type;

    if (isVideoMimeType(file.type)) {
      // For videos, clear preview immediately and upload directly
      this.currentFileUrl = undefined;
      this.updateVideoState();
      this.requestUpdate(); // Force re-render
      this.handleFileSelect(file);
    } else {
      // For images, store previous state before clearing and go to cropping
      this.previousFileUrl = this.currentFileUrl;
      this.previousValue = this._value;
      this.currentFileUrl = undefined;
      this.updateVideoState();
      this.requestUpdate(); // Force re-render
      this.loadImage(file);
    }
  }

  protected override render() {
    switch (this.currentState) {
      case "initial":
        return this.renderInitial();
      case "cropping":
        return this.renderCropping();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "media-uploader": MediaUploader;
  }
}
